import type { Moment } from "moment-timezone";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useState } from "react";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionDirection,
	type UtilityConsumptionReportCalendarMode,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";
import {
	fromUtilityConsumptionDateKey,
	getDefaultUtilityConsumptionSectionRange,
	moveUtilityConsumptionReportDate,
	toUtilityConsumptionDateKey,
} from "../utils/calendar";

const REPORT_PERIOD_QUERY = {
	report: parseAsStringLiteral(
		Object.values(UTILITY_CONSUMPTION_REPORT_MODE),
	).withDefault(UTILITY_CONSUMPTION_REPORT_MODE.PERIOD),
	rdate: parseAsString,
	sfrom: parseAsString,
	sto: parseAsString,
};

interface UseConsumptionReportPeriodInput {
	range: UtilityConsumptionReportRange;
	onRangeChange: (range: UtilityConsumptionReportRange) => void;
}

export interface ConsumptionReportPeriod {
	mode: UtilityConsumptionReportMode;
	isSection: boolean;
	referenceDate: Date;
	sectionEndBoundary: Date;
	sectionRange: UtilityConsumptionReportRange;
	activeRange: UtilityConsumptionReportRange;
	calendarMode: UtilityConsumptionReportCalendarMode | null;
	changeMode: (mode: UtilityConsumptionReportMode) => void;
	setReferenceDate: (referenceDate: Date) => void;
	setRangeDate: (
		field: keyof UtilityConsumptionReportRange,
		value: Moment | null,
	) => void;
	stepCalendar: (direction?: UtilityConsumptionDirection) => void;
}

export const useConsumptionReportPeriod = ({
	range,
	onRangeChange,
}: UseConsumptionReportPeriodInput): ConsumptionReportPeriod => {
	const [{ report: mode, rdate, sfrom, sto }, setQuery] =
		useQueryStates(REPORT_PERIOD_QUERY);

	const [sectionEndBoundary] = useState(() => new Date());

	const referenceDate = fromUtilityConsumptionDateKey(rdate, () => new Date());
	const defaultSectionRange = getDefaultUtilityConsumptionSectionRange();
	const sectionRange = {
		startDateKey: sfrom ?? defaultSectionRange.startDateKey,
		endDateKey: sto ?? defaultSectionRange.endDateKey,
	};

	const isSection = mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION;
	const calendarMode: UtilityConsumptionReportCalendarMode | null =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD || isSection ? null : mode;
	const activeRange = isSection ? sectionRange : range;

	return {
		mode,
		isSection,
		referenceDate,
		sectionEndBoundary,
		sectionRange,
		activeRange,
		calendarMode,
		changeMode: (nextMode) => setQuery({ report: nextMode, rdate: null }),
		setReferenceDate: (value) =>
			setQuery({ rdate: toUtilityConsumptionDateKey(value) }),
		setRangeDate: (field, value) => {
			if (!value?.isValid()) return;
			const nextRange = { ...activeRange, [field]: value.format("YYYY-MM-DD") };
			if (isSection)
				setQuery({
					sfrom: nextRange.startDateKey,
					sto: nextRange.endDateKey,
				});
			else onRangeChange(nextRange);
		},
		stepCalendar: (direction) => {
			if (calendarMode === null) return;
			setQuery({
				rdate:
					direction === undefined
						? null
						: toUtilityConsumptionDateKey(
								moveUtilityConsumptionReportDate(
									calendarMode,
									referenceDate,
									direction,
								),
							),
			});
		},
	};
};
